/**
 * DaySeparator — the day-boundary bar in a chat transcript: a centred label
 * between two hairlines, marking where a conversation crossed midnight.
 *
 * Presentation only, and deliberately so. Whether a boundary exists and what it
 * says are CALENDAR questions — they depend on the reader's timezone, on the
 * consumer's locale and copy ("Today" / "Yesterday" / "12 March"), and on a
 * clock that has to keep ticking so a conversation left open across midnight
 * does not keep a stale "Today" bar over yesterday's turns. None of that is
 * shape, so none of it lives here: the consumer resolves the label and hands it
 * over. Pass `null`/`undefined` and the bar renders nothing, so call sites stay
 * guard-free:
 *
 *   <DaySeparator label={dayLabel(prev?.at, msg.at)} />
 *
 * A real `role="separator"` carrying the label as its accessible name, so a
 * screen reader announces the boundary instead of reading a bare word floating
 * between two messages. The rules are `aria-hidden` — they are the drawing, and
 * the label is already the name.
 *
 * Composition with `ChatLog` / `MessageRow`:
 *
 *   {messages.map((m, i) => (
 *     <React.Fragment key={m.id}>
 *       <DaySeparator label={dayLabel(messages[i - 1]?.at, m.at)} />
 *       <MessageRow role={m.role}>{m.text}</MessageRow>
 *     </React.Fragment>
 *   ))}
 */
import * as React from "react";
import { cn } from "../lib/cn";
import { Skeleton } from "../primitives/Skeleton";

export interface DaySeparatorProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "children"> {
  /**
   * The resolved boundary label ("Today", "Yesterday", "12 March"). Nullish
   * renders nothing — the consumer's "no boundary here" answer.
   */
  label?: string | null;
  /**
   * Render the bar's geometry with a placeholder in place of the label, for a
   * transcript still hydrating. Distinct from the empty state: a nullish
   * `label` means "no boundary here" and renders NOTHING, while `loading` means
   * "there is a boundary, its words have not arrived" and holds the space so
   * the rows above and below do not jump when it resolves.
   */
  loading?: boolean;
}

export const DaySeparator = React.memo(
  React.forwardRef<HTMLDivElement, DaySeparatorProps>(function DaySeparator(
    { label, loading = false, className, ...props },
    ref,
  ) {
    if (loading) {
      return (
        <div
          ref={ref}
          aria-hidden="true"
          className={cn("flex items-center gap-2 py-1", className)}
          {...props}
        >
          <span className="h-px flex-1 bg-hairline" />
          <Skeleton className="h-3 w-16 rounded-full" />
          <span className="h-px flex-1 bg-hairline" />
        </div>
      );
    }
    if (!label) return null;
    return (
      <div
        ref={ref}
        role="separator"
        aria-label={label}
        className={cn("flex items-center gap-2 py-1", className)}
        {...props}
      >
        <span className="h-px flex-1 bg-hairline" aria-hidden="true" />
        <span className="whitespace-nowrap px-1.5 text-[11px] font-medium tracking-wide text-muted-foreground">
          {label}
        </span>
        <span className="h-px flex-1 bg-hairline" aria-hidden="true" />
      </div>
    );
  }),
);
